import * as THREE from 'three'

import steveSkin from '../default-textures-master/textures/entity/steve.png'
import alexSkin from '../default-textures-master/textures/entity/alex.png'
import type Terrain from '../terrain'
import { BlockType } from '../terrain'
import { InventoryItemType, isBlockType } from '../items'
import { blockIcons } from '../assets/block-icons'

type SkinName = 'Steve' | 'Alex'

interface SkinDefinition {
  name: SkinName
  texture: string
}

const SKINS: SkinDefinition[] = [
  { name: 'Steve', texture: steveSkin },
  { name: 'Alex', texture: alexSkin }
]

const TEX_WIDTH = 64
const TEX_HEIGHT = 64
const PX = 1.8 / 32

function setBoxUVs(
  geometry: THREE.BufferGeometry,
  faceUVArray: [number, number, number, number][]
) {
  const uvAttribute = geometry.attributes.uv
  for (let face = 0; face < 6; face++) {
    const rect = faceUVArray[face]
    const minU = rect[0] / TEX_WIDTH
    const minV = 1.0 - (rect[1] + rect[3]) / TEX_HEIGHT
    const maxU = (rect[0] + rect[2]) / TEX_WIDTH
    const maxV = 1.0 - rect[1] / TEX_HEIGHT
    const index = face * 4

    uvAttribute.setXY(index + 0, minU, maxV)
    uvAttribute.setXY(index + 1, maxU, maxV)
    uvAttribute.setXY(index + 2, minU, minV)
    uvAttribute.setXY(index + 3, maxU, minV)
  }
  uvAttribute.needsUpdate = true
}

function minecraftUVs(
  u: number,
  v: number,
  dx: number,
  dy: number,
  dz: number
): [number, number, number, number][] {
  return [
    [u + dz + dx, v + dz, dz, dy],
    [u, v + dz, dz, dy],
    [u + dz, v, dx, dz],
    [u + dz + dx, v, dx, dz],
    [u + dz, v + dz, dx, dy],
    [u + dz + dx + dz, v + dz, dx, dy]
  ]
}

export default class PlayerAvatar {
  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
    this.scene = scene
    this.camera = camera
    this.materials = SKINS.map(skin => this.createMaterial(skin.texture))
    this.avatarGroup.visible = false
    this.handGroup.visible = false
    this.buildAvatar()
    this.buildHand()
    this.scene.add(this.avatarGroup)
    this.scene.add(this.handGroup)
  }

  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  avatarGroup = new THREE.Group()
  handGroup = new THREE.Group()
  swingGroup = new THREE.Group()
  bodyParts: THREE.Mesh[] = []
  handParts: THREE.Mesh[] = []
  materials: THREE.MeshStandardMaterial[]
  skinIndex = 0
  thirdPerson = false
  terrain: Terrain | null = null
  heldType: InventoryItemType | null = null
  heldMesh: THREE.Mesh | null = null
  heldIconMaterials = new Map<InventoryItemType, THREE.MeshBasicMaterial>()
  miningPhase = 0
  savedPosition = new THREE.Vector3()
  savedQuaternion = new THREE.Quaternion()
  cameraWasMoved = false
  handOffsetScratch = new THREE.Vector3()
  cameraTargetScratch = new THREE.Vector3()
  cameraOffsetScratch = new THREE.Vector3()
  euler = new THREE.Euler(0, 0, 0, 'YXZ')
  yawEuler = new THREE.Euler(0, 0, 0, 'YXZ')
  yawQuaternion = new THREE.Quaternion()

  createMaterial(texturePath: string) {
    const texture = new THREE.TextureLoader().load(texturePath)
    texture.magFilter = THREE.NearestFilter
    texture.minFilter = THREE.NearestFilter
    return new THREE.MeshStandardMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.1,
      roughness: 0.9,
      metalness: 0
    })
  }

  createPart(
    width: number,
    height: number,
    depth: number,
    u: number,
    v: number,
    x: number,
    y: number,
    z: number
  ) {
    const geometry = new THREE.BoxGeometry(width * PX, height * PX, depth * PX)
    setBoxUVs(geometry, minecraftUVs(u, v, width, height, depth))
    const mesh = new THREE.Mesh(geometry, this.materials[this.skinIndex])
    mesh.position.set(x * PX, y * PX, z * PX)
    mesh.castShadow = true
    mesh.receiveShadow = true
    return mesh
  }

  buildAvatar() {
    const head = this.createPart(8, 8, 8, 0, 0, 0, 28, 0)
    const body = this.createPart(8, 12, 4, 16, 16, 0, 18, 0)
    const rightArm = this.createPart(4, 12, 4, 40, 16, -6, 18, 0)
    const leftArm = this.createPart(4, 12, 4, 32, 48, 6, 18, 0)
    const rightLeg = this.createPart(4, 12, 4, 0, 16, -2, 6, 0)
    const leftLeg = this.createPart(4, 12, 4, 16, 48, 2, 6, 0)

    this.bodyParts = [head, body, rightArm, leftArm, rightLeg, leftLeg]
    this.avatarGroup.add(...this.bodyParts)
  }

  buildHand() {
    const arm = this.createPart(4, 12, 4, 40, 16, 0, 0, 0)
    // Flip the hand so inner-side faces the body and the hand-end (not the shoulder-end)
    // sits at the far end of the arm. The avatar body gets this for free from the 180°
    // rotation on avatarGroup; the hand follows the camera with no such group flip.
    arm.geometry.rotateZ(Math.PI)
    arm.position.set(0.04, -0.08, 0.04)
    arm.rotation.set(-0.92, 0.42, -0.38)
    arm.scale.set(0.82, 0.82, 0.82)
    this.handParts = [arm]
    this.swingGroup.add(arm)
    this.handGroup.add(this.swingGroup)
  }

  toggleView() {
    this.thirdPerson = !this.thirdPerson
  }

  nextSkin() {
    this.skinIndex = (this.skinIndex + 1) % this.materials.length
    const material = this.materials[this.skinIndex]
    for (const part of [...this.bodyParts, ...this.handParts]) {
      part.material = material
    }
  }

  getSkinName() {
    return SKINS[this.skinIndex].name
  }

  setTerrain(terrain: Terrain) {
    this.terrain = terrain
  }

  setHeldItem(type: InventoryItemType | null) {
    if (type === this.heldType && (type !== null || !this.heldMesh)) return
    this.heldType = type

    if (this.heldMesh) {
      this.swingGroup.remove(this.heldMesh)
      this.heldMesh.geometry.dispose()
      this.heldMesh.visible = false
      this.heldMesh = null
    }

    if (type === null) return

    if (isBlockType(type) && type !== BlockType.bedrock && this.terrain) {
      const material = this.terrain.materials.get(
        this.terrain.materialType[type]
      )
      const geometry = new THREE.BoxGeometry(0.28, 0.28, 0.28)
      const mesh = new THREE.Mesh(geometry, material)
      mesh.position.set(0.05, -0.02, -0.5)
      mesh.rotation.set(0.22, -0.6, -0.1)
      this.heldMesh = mesh
      this.swingGroup.add(mesh)
      return
    }

    const iconUrl = blockIcons[type]
    if (iconUrl) {
      let iconMaterial = this.heldIconMaterials.get(type)
      if (!iconMaterial) {
        const texture = new THREE.TextureLoader().load(iconUrl)
        texture.magFilter = THREE.NearestFilter
        texture.minFilter = THREE.NearestFilter
        iconMaterial = new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          alphaTest: 0.1,
          side: THREE.DoubleSide
        })
        this.heldIconMaterials.set(type, iconMaterial)
      }
      const geometry = new THREE.PlaneGeometry(0.52, 0.52)
      const mesh = new THREE.Mesh(geometry, iconMaterial)
      mesh.position.set(0.08, -0.03, -0.53)
      mesh.rotation.set(-0.15, -0.52, -0.5)
      this.heldMesh = mesh
      this.swingGroup.add(mesh)
    }
  }

  update(
    active: boolean,
    delta: number,
    state: {
      isMoving?: boolean
      isSprinting?: boolean
      isFalling?: boolean
      isMining?: boolean
    } = {}
  ) {
    if (!active) {
      this.avatarGroup.visible = false
      this.handGroup.visible = false
      return
    }

    this.euler.setFromQuaternion(this.camera.quaternion)
    this.avatarGroup.position.set(
      this.camera.position.x,
      this.camera.position.y - 1.62,
      this.camera.position.z
    )
    this.avatarGroup.rotation.y = this.euler.y + Math.PI

    this.avatarGroup.visible = this.thirdPerson
    this.handGroup.visible = !this.thirdPerson

    const handOffset = this.handOffsetScratch
      .set(0.3, -0.35, -0.62)
      .applyQuaternion(this.camera.quaternion)
    this.handGroup.position.copy(this.camera.position).add(handOffset)
    this.handGroup.quaternion.copy(this.camera.quaternion)
    this.handGroup.rotation.z -= 0.08
    this.handGroup.rotation.x -= 0.18

    const t = performance.now() * 0.001

    // Pick step frequency / amplitudes based on movement state
    let stepFreq = 1.5
    let bobAmp = 0.005
    let swingAmp = 0.0
    if (state.isFalling) {
      stepFreq = 3
      bobAmp = 0
      swingAmp = 0
    } else if (state.isSprinting) {
      stepFreq = 13
      bobAmp = 0.06
      swingAmp = 0.32
    } else if (state.isMoving) {
      stepFreq = 8
      bobAmp = 0.025
      swingAmp = 0.16
    }

    this.handGroup.position.y += Math.sin(t * stepFreq) * bobAmp

    // Compose deltas onto the hand swing group
    let dx = Math.sin(t * stepFreq) * swingAmp
    let dy = 0
    let dz = 0

    if (state.isFalling) {
      // arm raised forward and waving
      dx -= 0.45 + Math.sin(t * 6) * 0.15
      dz = Math.sin(t * 4) * 0.22
    }

    if (state.isMining) {
      // chopping motion: smooth push down/up at ~5 Hz
      this.miningPhase += delta * 5 * Math.PI * 2
      const m = Math.sin(this.miningPhase)
      // Bias the swing forward/down (negative dx pulls the hand forward in our frame)
      dx -= 0.55 + m * 0.5
      dz += m * 0.15
    } else {
      this.miningPhase = 0
    }

    this.swingGroup.rotation.set(dx, dy, dz)

    // Body avatar (third-person) arm/leg swing
    let bodySwing = 0
    if (state.isFalling) {
      bodySwing = Math.sin(t * 6) * 0.4
      // raise both arms when falling
      this.bodyParts[2].rotation.x = -1.2 + Math.sin(t * 6) * 0.1
      this.bodyParts[3].rotation.x = -1.2 - Math.sin(t * 6) * 0.1
      this.bodyParts[4].rotation.x = bodySwing
      this.bodyParts[5].rotation.x = -bodySwing
    } else if (state.isSprinting) {
      bodySwing = Math.sin(t * 13) * 1.2
      this.bodyParts[2].rotation.x = bodySwing
      this.bodyParts[3].rotation.x = -bodySwing
      this.bodyParts[4].rotation.x = -bodySwing
      this.bodyParts[5].rotation.x = bodySwing
    } else if (state.isMoving) {
      bodySwing = Math.sin(t * 8) * 0.7
      this.bodyParts[2].rotation.x = bodySwing
      this.bodyParts[3].rotation.x = -bodySwing
      this.bodyParts[4].rotation.x = -bodySwing
      this.bodyParts[5].rotation.x = bodySwing
    } else {
      // idle subtle sway
      const idle = Math.sin(t * 1.5) * 0.04
      this.bodyParts[2].rotation.x = idle
      this.bodyParts[3].rotation.x = -idle
      this.bodyParts[4].rotation.x = 0
      this.bodyParts[5].rotation.x = 0
    }

    if (state.isMining) {
      // override right arm with mining chop
      this.bodyParts[2].rotation.x = -1.0 + Math.sin(this.miningPhase) * 0.6
    }
  }

  beforeRender() {
    if (!this.thirdPerson || !this.avatarGroup.visible) return

    this.savedPosition.copy(this.camera.position)
    this.savedQuaternion.copy(this.camera.quaternion)
    this.cameraWasMoved = true

    this.euler.setFromQuaternion(this.camera.quaternion)
    this.yawEuler.set(0, this.euler.y, 0)
    this.yawQuaternion.setFromEuler(this.yawEuler)

    const target = this.cameraTargetScratch.copy(this.savedPosition)
    target.y -= 0.15
    const offset = this.cameraOffsetScratch
      .set(0, 0.85, 4.2)
      .applyQuaternion(this.yawQuaternion)

    this.camera.position.copy(target).add(offset)
    this.camera.lookAt(target)
  }

  afterRender() {
    if (!this.cameraWasMoved) return
    this.camera.position.copy(this.savedPosition)
    this.camera.quaternion.copy(this.savedQuaternion)
    this.cameraWasMoved = false
  }
}
